/**
 * `hashState` — the engine's regression primitive and its anti-cheat primitive.
 *
 * ## Algorithm
 *
 * FNV-1a, 32 bits, over a byte stream this module writes itself. Every mix is
 * `Math.imul` and `^` on int32 values, so the result is a specified integer
 * operation on every JavaScript engine and every CPU: the same state hashes to
 * the same number in a browser, in Node and in a test (BASELINE invariant 1).
 *
 * What is deliberately *not* used:
 *
 * - **No `JSON.stringify`.** Property order is an implementation detail of how
 *   an object was built, and two states that are equal as games could serialise
 *   differently. The byte order here is written by hand, in one place.
 * - **No floating point.** Every number in a `GameState` is an integer: time
 *   is counted in ticks, and gravity in integer units (`config.ts`).
 *   `createConfig` refuses a config that is not, and `test/hash.test.ts`
 *   walks played-out states to check nothing became one.
 * - **No platform APIs.** No `TextEncoder`, no `Buffer`, no `crypto`.
 *
 * ## Exactness
 *
 * A number is written as eight bytes — its value as a 64-bit two's complement
 * integer, most significant byte first — so two different integers a state
 * can hold never write the same bytes. (`value | 0` would keep only the low
 * 32 bits, and would truncate a fraction: 3.1 and 3.9 would both write 3.)
 * The hash itself is 32 bits, so two different states can still collide by
 * chance; what cannot happen is two different states feeding it the same
 * stream.
 *
 * ## Coverage
 *
 * The hash covers the **full deterministic simulation state**, not just the
 * board (`docs/prompt-review.md` R3). In stream order:
 *
 * 1. `HASH_VERSION`
 * 2. the whole config — seed, width, visible rows, hidden rows, group gravity,
 *    lock delay, max lock resets, the queue preview depth (it sets when the
 *    bag refills), and the whole gravity curve with its speed
 * 3. the tick counter
 * 4. every board cell, top-left to bottom-right, hidden rows included
 * 5. the active pieces: how many, then each one's kind, rotation, position,
 *    lock timer, lock resets, gravity accumulator and rotation flag
 * 6. the hold slots: each held piece and whether hold is still available
 * 7. the randomiser: the PRNG word, then the whole drawn queue
 * 8. the garbage queue: how many batches, then each batch's id, lines and
 *    ready tick, and the id the next batch will take
 * 9. the counters that change future attack: combo, back-to-back, lines,
 *    attack, pieces placed, best chain, garbage hole column, top-out flag
 *
 * Two simulations that agree on all of that cannot diverge later; two that
 * disagree on any of it write different streams. Adding a field means adding
 * it here and bumping `HASH_VERSION`.
 */
import type { Cell, GameState, PieceKind } from './types.js';

/**
 * Bumped whenever the byte stream changes shape.
 *
 * A recorded hash is only comparable against hashes of the same version, so the
 * version is the first thing in the stream. `2` is integer gravity and
 * eight-byte numbers (`docs/decisions/0050`).
 */
export const HASH_VERSION = 2;

const TWO_TO_THE_32 = 0x1_0000_0000;

const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

/** Cell codes. `null` is 0, the seven letters are 1..7, garbage is 8. */
const CELL_CODES: Readonly<Record<PieceKind | 'G', number>> = {
  I: 1,
  J: 2,
  L: 3,
  O: 4,
  S: 5,
  T: 6,
  Z: 7,
  G: 8,
};

function cellCode(cell: Cell): number {
  if (cell === null) return 0;
  return CELL_CODES[cell];
}

/** Mix one byte in. */
function mixByte(hash: number, byte: number): number {
  return Math.imul(hash ^ (byte & 0xff), FNV_PRIME) >>> 0;
}

/** Mix one 32-bit word in, most significant byte first. */
function mixWord(hash: number, word: number): number {
  let next = mixByte(hash, word >>> 24);
  next = mixByte(next, word >>> 16);
  next = mixByte(next, word >>> 8);
  return mixByte(next, word);
}

/**
 * Mix an integer in exactly, as a 64-bit two's complement value: the high
 * word, then the low word.
 *
 * `Math.floor(value / 2^32)` is exact for every safe integer — dividing by a
 * power of two only changes the exponent — and `>>> 0` is the value modulo
 * 2^32, so the pair identifies the integer. Negative numbers come out as
 * two's complement: -1 is eight `0xff` bytes.
 */
function mixInt(hash: number, value: number): number {
  const high = Math.floor(value / TWO_TO_THE_32);
  return mixWord(mixWord(hash, high >>> 0), value >>> 0);
}

/** Mix a flag in as a single byte. */
function mixFlag(hash: number, flag: boolean): number {
  return mixByte(hash, flag ? 1 : 0);
}

/** The full-state hash. See the module comment for exactly what it covers. */
export function hashState(state: GameState): number {
  const config = state.config;
  let hash = mixInt(FNV_OFFSET_BASIS, HASH_VERSION);

  // 2 — configuration that shapes play.
  hash = mixInt(hash, config.seed);
  hash = mixInt(hash, config.width);
  hash = mixInt(hash, config.visibleRows);
  hash = mixInt(hash, config.hiddenRows);
  hash = mixFlag(hash, config.groupGravity);
  hash = mixInt(hash, config.lockDelayMs);
  hash = mixInt(hash, config.maxLockResets);
  hash = mixInt(hash, config.nextPreview);
  hash = mixInt(hash, config.gravity.baseMs);
  hash = mixInt(hash, config.gravity.perLevelMs);
  hash = mixInt(hash, config.gravity.minMs);
  hash = mixInt(hash, config.gravity.linesPerLevel);
  hash = mixInt(hash, config.gravity.speedPermille);

  // 3 — where in time we are.
  hash = mixInt(hash, state.tick);

  // 4 — the board, hidden rows included.
  for (const row of state.board.rows) {
    for (const cell of row) hash = mixByte(hash, cellCode(cell));
  }

  // 5 — active pieces. The length is written first so a one-slot well and a
  //     two-slot well cannot write the same stream.
  hash = mixInt(hash, state.pieces.length);
  for (const piece of state.pieces) {
    if (piece === null) {
      hash = mixFlag(hash, false);
      continue;
    }
    hash = mixFlag(hash, true);
    hash = mixByte(hash, CELL_CODES[piece.kind]);
    hash = mixByte(hash, piece.rotation);
    hash = mixInt(hash, piece.x);
    hash = mixInt(hash, piece.y);
    hash = mixInt(hash, piece.lockTicks);
    hash = mixInt(hash, piece.lockResets);
    hash = mixInt(hash, piece.gravityUnits);
    hash = mixFlag(hash, piece.lastActionWasRotation);
  }

  // 6 — hold, and whether it has been spent on this piece.
  hash = mixInt(hash, state.holds.length);
  for (const held of state.holds) hash = mixByte(hash, cellCode(held ?? null));
  hash = mixInt(hash, state.holdAvailable.length);
  for (const available of state.holdAvailable) hash = mixFlag(hash, available);

  // 7 — the randomiser: its word, and everything already drawn from it.
  hash = mixInt(hash, state.rng.seed);
  hash = mixInt(hash, state.queue.length);
  for (const kind of state.queue) hash = mixByte(hash, CELL_CODES[kind]);

  // 8 — pending garbage.
  hash = mixInt(hash, state.pendingGarbage.length);
  for (const entry of state.pendingGarbage) {
    hash = mixInt(hash, entry.id);
    hash = mixInt(hash, entry.lines);
    hash = mixInt(hash, entry.readyAtTick);
  }
  hash = mixInt(hash, state.nextGarbageId);

  // 9 — counters that change what future clears are worth.
  hash = mixInt(hash, state.combo);
  hash = mixFlag(hash, state.backToBack);
  hash = mixInt(hash, state.lines);
  hash = mixInt(hash, state.attack);
  hash = mixInt(hash, state.piecesPlaced);
  hash = mixInt(hash, state.bestChain);
  hash = mixInt(hash, state.garbageColumn);
  hash = mixFlag(hash, state.topOut);

  return hash >>> 0;
}

/** `hashState` as eight lowercase hex digits. This is the fixture format. */
export function hashStateHex(state: GameState): string {
  return hashState(state).toString(16).padStart(8, '0');
}
